import { createSignal, createEffect, onCleanup, onMount } from 'solid-js';
import AudioChatReceived from '~/assets/sounds/chatReceived.mp3';
import { useSignalR } from '~/contexts/infrastructures';
import { createMagicDoorContext } from '~/contexts/utils';
import { MessageType } from '~/swagger/portal/Api';
import { normalizeChatNote } from '~/swagger/portal/chatNotes';
import { toLowerCaseFirstLetter } from '~/utils/tool';
import type { ChatNotification } from './chatNotification';
import type { ChatMessageDto, ChatNoteDto } from '~/swagger/portal/Api';
import type { ChatAssignmentChange, ChatHumanEngagementChange } from '~/swagger/portal/chatNotes';

const parseNotePayload = (rawNote: unknown): Record<string, unknown> =>
  typeof rawNote === 'string' ? JSON.parse(rawNote) : (rawNote as Record<string, unknown>);

interface GlobalChatContextValue {
  registerMessageReceivedCallback: (callback: (notification: ChatNotification) => void) => void;
  registerNoteUpsertedCallback: (callback: (note: ChatNoteDto) => void) => void;
  registerNoteDeletedCallback: (callback: (noteId: string) => void) => void;
  registerHumanEngagementCallback: (callback: (change: ChatHumanEngagementChange) => void) => void;
  registerChatAssignmentCallback: (callback: (change: ChatAssignmentChange) => void) => void;
  registerReconnectedCallback: (callback: () => void) => void;
}

export const [GlobalChatProvider, useGlobalChat] = createMagicDoorContext<GlobalChatContextValue>('GlobalChat', () => {
  const signalRService = useSignalR();

  const [messageCallbacks, setMessageCallbacks] = createSignal<((notification: ChatNotification) => void)[]>([]);
  const [noteUpsertedCallbacks, setNoteUpsertedCallbacks] = createSignal<((note: ChatNoteDto) => void)[]>([]);
  const [noteDeletedCallbacks, setNoteDeletedCallbacks] = createSignal<((noteId: string) => void)[]>([]);
  const [humanEngagementCallbacks, setHumanEngagementCallbacks] = createSignal<((change: ChatHumanEngagementChange) => void)[]>([]);
  const [chatAssignmentCallbacks, setChatAssignmentCallbacks] = createSignal<((change: ChatAssignmentChange) => void)[]>([]);
  const [reconnectedCallbacks, setReconnectedCallbacks] = createSignal<(() => void)[]>([]);
  const [userHasInteracted, setUserHasInteracted] = createSignal<boolean>(false);
  let currentMessageCallbacks: ((notification: ChatNotification) => void)[] = [];
  let currentNoteUpsertedCallbacks: ((note: ChatNoteDto) => void)[] = [];
  let currentNoteDeletedCallbacks: ((noteId: string) => void)[] = [];
  let currentHumanEngagementCallbacks: ((change: ChatHumanEngagementChange) => void)[] = [];
  let currentChatAssignmentCallbacks: ((change: ChatAssignmentChange) => void)[] = [];
  let currentReconnectedCallbacks: (() => void)[] = [];
  let currentUserHasInteracted = false;

  const registerMessageReceivedCallback = (callback: (notification: ChatNotification) => void) => {
    setMessageCallbacks((prev) => [...prev, callback]);
  };

  const registerNoteUpsertedCallback = (callback: (note: ChatNoteDto) => void) => {
    setNoteUpsertedCallbacks((prev) => [...prev, callback]);
  };

  const registerNoteDeletedCallback = (callback: (noteId: string) => void) => {
    setNoteDeletedCallbacks((prev) => [...prev, callback]);
  };

  const registerHumanEngagementCallback = (callback: (change: ChatHumanEngagementChange) => void) => {
    setHumanEngagementCallbacks((prev) => [...prev, callback]);
  };

  const registerChatAssignmentCallback = (callback: (change: ChatAssignmentChange) => void) => {
    setChatAssignmentCallbacks((prev) => [...prev, callback]);
  };

  const registerReconnectedCallback = (callback: () => void) => {
    setReconnectedCallbacks((prev) => [...prev, callback]);
  };

  function playNotificationSound() {
    const chatReceivedAudio = new Audio(AudioChatReceived);
    chatReceivedAudio.play().catch((error) => console.error('Error playing sound:', error));
  }

  onMount(() => {
    const handleFirstUserInteraction = () => {
      setUserHasInteracted(true);
      window.removeEventListener('click', handleFirstUserInteraction);
      window.removeEventListener('keydown', handleFirstUserInteraction);
    };

    window.addEventListener('click', handleFirstUserInteraction);
    window.addEventListener('keydown', handleFirstUserInteraction);
  });

  createEffect(() => {
    currentMessageCallbacks = messageCallbacks();
    currentNoteUpsertedCallbacks = noteUpsertedCallbacks();
    currentNoteDeletedCallbacks = noteDeletedCallbacks();
    currentHumanEngagementCallbacks = humanEngagementCallbacks();
    currentChatAssignmentCallbacks = chatAssignmentCallbacks();
    currentReconnectedCallbacks = reconnectedCallbacks();
    currentUserHasInteracted = userHasInteracted();
  });

  createEffect(() => {
    if (signalRService) {
      signalRService.onReconnected(() => {
        currentReconnectedCallbacks.forEach((cb) => cb());
      });
      signalRService
        .startConnection()
        .then(() => {
          signalRService.onReceiveMessage((rawMessage) => {
            const message: ChatMessageDto = typeof rawMessage === 'string' ? JSON.parse(rawMessage) : rawMessage;

            message.messageType = toLowerCaseFirstLetter(message.messageType) as MessageType;

            const allowedMessageTypes = [MessageType.Participant, 'unknown'];
            const isAllowedMessageType = allowedMessageTypes.includes(message.messageType);
            const shouldPlaySound = currentUserHasInteracted && isAllowedMessageType;

            if (shouldPlaySound) {
              playNotificationSound();
            }

            const notification: ChatNotification = {
              chatId: message.chatId,
              messageType: message.messageType,
            };

            currentMessageCallbacks.forEach((cb) => cb(notification));
          });
          signalRService.onChatCreate((chatId) => {
            const notification: ChatNotification = {
              chatId,
              messageType: MessageType.Announcement,
            };
            currentMessageCallbacks.forEach((cb) => cb(notification));
          });
          signalRService.onReceiveNote((rawNote) => {
            const note = normalizeChatNote(parseNotePayload(rawNote));
            currentNoteUpsertedCallbacks.forEach((cb) => cb(note));
          });
          signalRService.onNoteUpdated((rawNote) => {
            const note = normalizeChatNote(parseNotePayload(rawNote));
            currentNoteUpsertedCallbacks.forEach((cb) => cb(note));
          });
          signalRService.onNoteDeleted((noteId) => {
            currentNoteDeletedCallbacks.forEach((cb) => cb(noteId));
          });
          signalRService.onChatHumanEngagementChanged((chatId, requested, requestedAt) => {
            currentHumanEngagementCallbacks.forEach((cb) => cb({ chatId, requested, requestedAt }));
          });
          signalRService.onChatAssignmentChanged((chatId, assignedPropertyManagerId, assignedByPropertyManagerId, assignedAt) => {
            currentChatAssignmentCallbacks.forEach((cb) =>
              cb({ chatId, assignedPropertyManagerId, assignedByPropertyManagerId, assignedAt })
            );
          });
        })
        .catch((error) => console.error('SignalR connection error:', error));

      onCleanup(() => {
        signalRService.stopConnection().catch(console.error);
      });
    }
  });

  return {
    registerMessageReceivedCallback,
    registerNoteUpsertedCallback,
    registerNoteDeletedCallback,
    registerHumanEngagementCallback,
    registerChatAssignmentCallback,
    registerReconnectedCallback,
  };
});
